import { elements } from '../../../dom';
import { bindDropZone, bindFullscreenDropZone } from '../drag-drop';

type MediaPickerOptions = {
  isAudioDisabled: () => boolean;
  onAudioSelected: (file: File) => void;
  onClearImage: () => void;
  onFilesDropped: (files: FileList) => void;
  onImageSelected: (file: File) => void;
};

export function createMediaPicker({
  isAudioDisabled,
  onAudioSelected,
  onClearImage,
  onFilesDropped,
  onImageSelected,
}: MediaPickerOptions) {
  let hasAudioFile = false;
  let hasImageFile = false;
  let setupDisabled = false;

  elements.audioInput.addEventListener('change', () => {
    if (isAudioLocked()) {
      elements.audioInput.value = '';
      return;
    }

    const file = elements.audioInput.files?.[0];
    if (file) {
      onAudioSelected(file);
    }
  });

  elements.imageInput.addEventListener('change', () => {
    if (setupDisabled) {
      elements.imageInput.value = '';
      return;
    }

    const file = elements.imageInput.files?.[0];
    if (file) {
      onImageSelected(file);
    }
  });

  bindDropZone(elements.dropZone, 'audio/', (file, files) => {
    if (isAudioLocked()) {
      return;
    }

    elements.audioInput.files = files;
    onAudioSelected(file);
  });

  bindDropZone(elements.imageDropZone, 'image/', (file, files) => {
    if (setupDisabled) {
      return;
    }

    elements.imageInput.files = files;
    onImageSelected(file);
  });

  bindFullscreenDropZone((files) => {
    if (!setupDisabled) {
      onFilesDropped(files);
    }
  }, () => setupDisabled);
  elements.clearImageButton.addEventListener('click', () => {
    if (!setupDisabled) {
      onClearImage();
    }
  });

  return {
    resetAudio() {
      hasAudioFile = false;
      elements.audioInput.value = '';
      elements.fileLabel.textContent = 'Select audio';
      elements.audioInput.setAttribute('aria-label', 'Select audio file');
      syncDisabledState();
    },
    resetImage() {
      hasImageFile = false;
      elements.imageInput.value = '';
      elements.imageLabel.textContent = 'Add image';
      elements.imageInput.setAttribute('aria-label', 'Add image file');
      elements.clearImageButton.setAttribute('aria-label', 'Remove image');
      syncDisabledState();
    },
    setAudioDisabled(isDisabled: boolean) {
      syncDisabledState(isDisabled);
    },
    setAudioFile(file: File) {
      hasAudioFile = true;
      elements.fileLabel.textContent = file.name;
      elements.audioInput.setAttribute('aria-label', `Selected audio: ${file.name}. Choose another audio file`);
      syncDisabledState();
    },
    setImageFile(file: File) {
      hasImageFile = true;
      elements.imageLabel.textContent = file.name;
      elements.imageInput.setAttribute('aria-label', `Selected image: ${file.name}. Choose another image file`);
      elements.clearImageButton.setAttribute('aria-label', `Remove image: ${file.name}`);
      syncDisabledState();
    },
    setSetupDisabled(isDisabled: boolean) {
      setupDisabled = isDisabled;
      syncDisabledState();
    },
  };

  function isAudioLocked() {
    return setupDisabled || isAudioDisabled();
  }

  function syncDisabledState(audioDisabled = isAudioDisabled()) {
    const audioLocked = setupDisabled || audioDisabled;
    elements.audioInput.disabled = audioLocked;
    elements.dropZone.classList.toggle('is-disabled', audioLocked);
    elements.dropZone.classList.toggle('needs-audio', !hasAudioFile && !audioLocked);
    elements.dropZone.setAttribute('aria-disabled', String(audioLocked));

    elements.imageInput.disabled = setupDisabled;
    elements.imageDropZone.classList.toggle('is-disabled', setupDisabled);
    elements.imageDropZone.setAttribute('aria-disabled', String(setupDisabled));
    elements.clearImageButton.disabled = setupDisabled || !hasImageFile;
    elements.clearImageButton.setAttribute('aria-disabled', String(elements.clearImageButton.disabled));
  }
}
